import { Box } from '@factorialco/deprecated-design-system'
import { OnePagination, useDataCollectionData } from '@factorialco/f0-react/dist/experimental'

import type Employee from 'modules/employees/store/models/Employee'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import type { EmployeeEstimatedPeriodBaseSalary } from 'modules/payroll_bundle/hooks/useEmployeeEstimatedPeriodBaseSalary'
import type { ContractTaxonomyFragment } from 'generated/resources/graphql'
import type { useSupplementsSalariesDataCollectionSource } from 'modules/payroll_bundle/components/Supplements/useSupplementsSalariesDataCollectionSource'

import { SupplementTableRowCell } from './SupplementTableRowCell'

type SourceType = ReturnType<typeof useSupplementsSalariesDataCollectionSource>

export interface SupplementsCustomVisualizationProps {
  source: SourceType
  employeesById: Record<string, Employee>
  supplementsStore: SupplementsFormStore
  loadingSupplements: boolean
  taxonomies: ContractTaxonomyFragment[]
  itemToHighlight?: { employeeId?: string; supplementId?: string } | null
  employeeExpansionState: Record<string, boolean>
  toggleEmployeeExpansion: (id: string) => void
  toggleTaxonomyCreationModal: (value: boolean) => void
}

/**
 * Custom DataCollection visualization that renders the same data as the old
 * supplements table: one row per employee with their supplement forms.
 *
 * Uses employeeEstimatedPeriodBaseSalariesConnection as the data source, which
 * ensures pagination counts match the rendered rows (both are filtered by
 * policy period ID and employee filters).
 *
 * The records contain salary data including employeeId, which we use to:
 * 1. Look up the full Employee model from employeesById for rendering
 * 2. Get the estimated salary directly from the record
 */
export function SupplementsCustomVisualization({
  source,
  employeesById,
  supplementsStore,
  loadingSupplements,
  taxonomies,
  itemToHighlight,
  employeeExpansionState,
  toggleEmployeeExpansion,
  toggleTaxonomyCreationModal,
}: SupplementsCustomVisualizationProps) {
  const { data: collectionData, paginationInfo, setPage, isLoading } = useDataCollectionData(source)
  const records = collectionData?.records ?? []

  const pageBasedInfo =
    paginationInfo &&
    'type' in paginationInfo &&
    paginationInfo.type === 'pages' &&
    'currentPage' in paginationInfo &&
    'pagesCount' in paginationInfo
      ? paginationInfo
      : null

  return (
    <Box width="full" gap="s8">
      {records.map((record, index) => {
        const employee = employeesById[record.employeeId]
        const isLast = index === records.length - 1
        // Convert salary record to the expected format
        const estimatedSalary: EmployeeEstimatedPeriodBaseSalary | undefined = record
          ? {
              id: record.id,
              employeeId: record.employeeId,
              baseSalaryAmount: record.baseSalaryAmount,
              baseSalaryPeriodicity: record.baseSalaryPeriodicity,
              periodBaseSalaryAmount: record.periodBaseSalaryAmount,
            }
          : undefined
        return (
          <SupplementTableRowCell
            key={record.employeeId}
            employee={employee}
            isLast={isLast}
            supplementsStore={supplementsStore}
            loading={loadingSupplements}
            taxonomies={taxonomies}
            estimatedSalary={estimatedSalary}
            itemToHighlight={itemToHighlight}
            shouldScrollIntoView={null}
            employeeExpansionState={employeeExpansionState}
            toggleEmployeeExpansion={toggleEmployeeExpansion}
            toggleTaxonomyCreationModal={toggleTaxonomyCreationModal}
          />
        )
      })}
      {pageBasedInfo !== null && (
        <OnePagination
          totalPages={pageBasedInfo.pagesCount}
          currentPage={pageBasedInfo.currentPage}
          onPageChange={setPage}
          disabled={isLoading}
        />
      )}
    </Box>
  )
}
